<template>
  <div class="admin-room-container">
    <el-row :gutter="16">
      <!-- 左侧楼栋-单元树：保留原有 selectCommunity / selectFloor / selectUnit 联动 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-tree-card">
          <div class="ds-tree-card__header">{{ $t('adminRoom.tree.title') }}</div>
          <div class="ds-tree-card__body">
            <community-unit-tree ref="communityUnitTree" @selectCommunity="handleSelectCommunity"
              @selectFloor="handleSelectFloor" @selectUnit="handleSelectUnit" />
          </div>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('adminRoom.page.title')" :subtitle="scopeText" />

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.roomNum" clearable class="w-full"
                :placeholder="$t('adminRoom.search.roomNum')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.ownerNameLike" clearable class="w-full"
                :placeholder="$t('adminRoom.search.ownerName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.ownerTel" clearable class="w-full"
                :placeholder="$t('adminRoom.search.ownerTel')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.state" clearable class="w-full" :placeholder="$t('adminRoom.search.state')">
                <el-option v-for="item in states" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.roomSubType" clearable class="w-full"
                :placeholder="$t('adminRoom.search.roomType')">
                <el-option v-for="item in roomSubTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminRoom.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminRoom.page.empty')" @retry="getList">
            <el-table-column prop="roomNum" :label="$t('adminRoom.table.room')" fixed="left" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.roomNum || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="communityName" :label="$t('adminRoom.table.community')" min-width="120" />
            <el-table-column prop="layer" :label="$t('adminRoom.table.floor')" width="90" align="center">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.layer }}{{ $t('adminRoom.table.floorUnit') }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="ownerName" :label="$t('adminRoom.table.owner')" min-width="140">
              <template slot-scope="scope">
                <router-link v-if="scope.row.ownerName"
                  :to="`/views/aCommunity/adminOwnerDetail?ownerId=${scope.row.ownerId}`">
                  {{ scope.row.ownerName }}({{ scope.row.link }})
                </router-link>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="roomSubTypeName" :label="$t('adminRoom.table.type')" width="100" align="center" />
            <el-table-column :label="$t('adminRoom.table.area')" width="130" align="right">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.builtUpArea }}/{{ scope.row.roomArea }}{{
                  $t('adminRoom.table.areaUnit') }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="roomRent" :label="$t('adminRoom.table.rent')" width="110" align="right">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.roomRent || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="stateName" :label="$t('adminRoom.table.state')" width="110" align="center">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminRoom.table.validDate')" width="140" align="center">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
                <div class="ds-num">{{ scope.row.endTime || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="memberCount" :label="$t('adminRoom.table.member')" width="100" align="center">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatCount(scope.row.memberCount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="carCount" :label="$t('adminRoom.table.car')" width="100" align="center">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatCount(scope.row.carCount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="roomCount" :label="$t('adminRoom.table.ownerRoom')" width="100" align="center">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatCount(scope.row.roomCount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="contractCount" :label="$t('adminRoom.table.contract')" width="100" align="center">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatCount(scope.row.contractCount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" fixed="right" width="100" align="center">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="handleDetail(scope.row)">
                  {{ $t('common.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('adminRoom.page.tip') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { queryAdminRoom, getRoomStates, getRoomSubTypes } from '@/api/community/adminRoomApi'
import CommunityUnitTree from '@/components/community/CommunityUnitTree'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminRoomList',
  components: {
    CommunityUnitTree,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        communityId: '',
        floorId: '',
        unitId: '',
        ownerNameLike: '',
        ownerTel: '',
        roomNum: '',
        state: '',
        roomSubType: '',
        page: 1,
        row: 10
      },
      tableData: [],
      states: [],
      roomSubTypes: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    /**
     * 副标题：当前查询范围（小区 / 楼层 / 单元）
     */
    scopeText() {
      const form = this.searchForm
      if (form.communityId) return this.$t('adminRoom.page.scopeCommunity')
      if (form.floorId) return this.$t('adminRoom.page.scopeFloor')
      if (form.unitId) return this.$t('adminRoom.page.scopeUnit')
      return this.$t('adminRoom.page.subtitle')
    }
  },
  created() {
    this.initData()
    this.getList()
  },
  methods: {
    async initData() {
      try {
        const [statesRes, typesRes] = await Promise.all([
          getRoomStates(),
          getRoomSubTypes()
        ])
        this.states = statesRes
        this.roomSubTypes = typesRes
        this.$refs.communityUnitTree.open()
      } catch (error) {
        this.$message.error(this.$t('common.fetchError'))
      }
    },
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { data, total } = await queryAdminRoom(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取房屋数据失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('adminRoom.page.loadFailed')
        this.$message.error(this.$t('adminRoom.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    /**
     * 重置：清空筛选条件（保留左侧树选中范围，语义与原页面一致）
     */
    handleReset() {
      this.searchForm.roomNum = ''
      this.searchForm.ownerNameLike = ''
      this.searchForm.ownerTel = ''
      this.searchForm.state = ''
      this.searchForm.roomSubType = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSelectCommunity({ communityId }) {
      this.searchForm.communityId = communityId
      this.searchForm.floorId = ''
      this.searchForm.unitId = ''
      this.getList()
    },
    handleSelectFloor({ floorId }) {
      this.searchForm.communityId = ''
      this.searchForm.floorId = floorId
      this.searchForm.unitId = ''
      this.getList()
    },
    handleSelectUnit({ unitId }) {
      this.searchForm.communityId = ''
      this.searchForm.floorId = ''
      this.searchForm.unitId = unitId
      this.getList()
    },
    handleDetail(row) {
      this.$router.push(`/pages/community/adminRoomDetail?roomId=${row.roomId}`)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    /**
     * 房屋状态语义类型（building_room.state）
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'room',
        text: row.stateName || ''
      }).type
    },
    /**
     * 计数格式化：空值统一显示 0（只改展示，不改原值）
     */
    formatCount(value) {
      if (value === null || value === undefined || value === '') return 0
      return value
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-room-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧楼栋-单元树卡片 */
.ds-tree-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__body {
    padding: var(--ds-spacing-sm);
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
